import { ThemeProvider } from "styled-components";
import GlobalStyles from "./styles/GlobalStyles";
import { dark } from "./styles/Themes";
import { LocomotiveScrollProvider } from "react-locomotive-scroll";
import { useEffect, useRef, useState } from "react";
import 'locomotive-scroll/dist/locomotive-scroll.css'
import { AnimatePresence } from "framer-motion";
import ScrollTriggerProxy from './components/ScrollTriggerProxy';
import Loader from "./components/Loader";
import ScrollToTop from "./components/ScrollToTop";
import ProjectDetailPage from "./pages/ProjectDetailPage";

import { Routes, Route, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import ProjectsPage from "./pages/projects";
import ContactPage from "./pages/ContactPage";


function App() {
  const containerRef = useRef(null);
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);

  // Your existing nuclear option effect
  useEffect(() => {
    const navigation = performance.getEntriesByType('navigation')[0];
    const isRefresh = navigation && navigation.type === 'reload';
    const isRefreshLegacy = performance.navigation && performance.navigation.type === 1;
    
    if (isRefresh || isRefreshLegacy) {
      console.log('Page refresh detected - applying nuclear option');
      
      sessionStorage.removeItem('locomotive_initialized');
      sessionStorage.removeItem('gsap_initialized');
      window.scrollTo(0, 0);
      sessionStorage.setItem('force_clean_init', 'true');
      
      const hasForceReloaded = sessionStorage.getItem('has_force_reloaded');
      
      if (!hasForceReloaded) {
        sessionStorage.setItem('has_force_reloaded', 'true');
        setTimeout(() => {
          window.location.reload();
        }, 100);
        return;
      } else {
        setTimeout(() => {
          sessionStorage.removeItem('has_force_reloaded');
          sessionStorage.removeItem('force_clean_init');
        }, 1000);
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo(0, 0);
          }
        }, 100);
      }
    };

    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('Page loaded from cache - forcing reload');
        window.location.reload();
      }
    };

    const handleBeforeUnload = () => {
      sessionStorage.setItem('page_unloading', 'true');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true);
    }, 3000);
  }, []);

  // Force window scroll effect
  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0);
      if (containerRef.current) {
        containerRef.current.scrollTo(0, 0);
      }
    };

    resetScroll();
    const timeouts = [10, 50, 100, 200, 500].map(delay =>
      setTimeout(resetScroll, delay)
    );

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <>
      <GlobalStyles />
      <ThemeProvider theme={dark}>
        <LocomotiveScrollProvider
          options={{
            smooth: true,
            smartphone: { smooth: true },
            tablet: { smooth: true },
            resetNativeScroll: true,
            getDirection: true,
            getSpeed: true,
            class: 'is-reveal'
          }}
          watch={[location.pathname, loaded]}
          containerRef={containerRef}
          onLocationChange={(scroll) => {
            if (scroll) {
              window.locomotive = scroll;
              
              if (location.pathname !== '/') {
                console.log('LocomotiveScrollProvider: Forcing scroll to top');
                
                scroll.scrollTo(0, { duration: 0, disableLerp: true, immediate: true });
                
                setTimeout(() => {
                  scroll.scrollTo(0, { duration: 0, disableLerp: true, immediate: true });
                }, 10);
                
                setTimeout(() => {
                  scroll.scrollTo(0, { duration: 0, disableLerp: true, immediate: true });
                }, 100);
                
                setTimeout(() => {
                  scroll.scrollTo(0, { duration: 0, disableLerp: true, immediate: true });
                }, 200);
              }
            }
          }}
          onUpdate={(scroll) => {
            if (scroll) {
              window.locomotive = scroll;
            }
            
            if (containerRef.current) {
              const isForceCleanInit = sessionStorage.getItem('force_clean_init');
              if (isForceCleanInit && scroll) {
                scroll.scrollTo(0, { duration: 0, disableLerp: true, immediate: true });
              }
            }
          }}
        >
          <AnimatePresence mode="wait">
            {loaded ? null : <Loader />}
          </AnimatePresence>
          <ScrollTriggerProxy />
          <AnimatePresence mode="wait">
            <main className='App' data-scroll-container ref={containerRef}>
              <ScrollToTop 
                containerRef={containerRef} 
                excludeRoutes={['/']} 
              />
              
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<HomePage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route 
                    path="/projects/:projectId" 
                    element={<ProjectDetailPage />} 
                />
                <Route path="/contact" element={<ContactPage />} />

              </Routes>
            </main>
          </AnimatePresence>
        </LocomotiveScrollProvider>
      </ThemeProvider>
    </>
  );
}

export default App;